import React, { FC, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SafeECharts from './SafeECharts';

import DrawerMenu from 'components/DrawerMenu';
import DrawerContent from './DrawerContent';
import { formatCompactIndianCurrency } from '../utils/formatters';
import { getCurrencySymbol } from 'utils/hooks/getCurrency';

type CategoryKey = 'A' | 'B' | 'C' | 'O';

interface ManagerAccountSummaryRow {
  managerName?: string;
  categorySummaryMap?: Partial<
    Record<CategoryKey, { count?: number; roomNights?: number; companies?: any[] }>
  >;
  total?: number;
}

interface ManagerAccountSummaryChartProps {
  data: ManagerAccountSummaryRow[];
  onCategoryClick?: (category: CategoryKey, companies: any[], managerName: string) => void;
  formatNumber?: (value: number) => string;
  emptyMessage?: string;
  // Map of manager name (lowercased) -> sort index, to keep ordering consistent with Manager Performance.
  managerOrder?: Map<string, number>;
}

const TOP_MANAGERS_LIMIT = 5;

const CATEGORY_META: Array<{ key: CategoryKey; label: string; color: string }> = [
  { key: 'A', label: 'A - High', color: '#2563EB' },
  { key: 'B', label: 'B - Medium', color: '#059669' },
  { key: 'C', label: 'C - Low', color: '#D97706' },
  { key: 'O', label: 'O - Others', color: '#7C3AED' },
];

const TOTAL_ROW_NAME = 'Total';

const defaultFormatNumber = (value: number): string => Number(value).toLocaleString('en-IN');

const ManagerAccountSummaryChart: FC<ManagerAccountSummaryChartProps> = ({
  data,
  onCategoryClick,
  formatNumber = defaultFormatNumber,
  emptyMessage = 'No account summary found',
  managerOrder,
}) => {
  const [fullReportOpen, setFullReportOpen] = useState(false);
  const [viewAllOpen, setViewAllOpen] = useState(false);

  const currencySymbol = getCurrencySymbol();
  const formatMoney = (value: number) => formatCompactIndianCurrency(value, currencySymbol);

  const totalAccounts = (row: ManagerAccountSummaryRow): number =>
    CATEGORY_META.reduce((sum, cat) => sum + Number(row?.categorySummaryMap?.[cat.key]?.count ?? 0), 0);

  // Exclude the aggregate "Total" row and sort to match Manager Performance ordering.
  const managers = useMemo(() => {
    const rows = (Array.isArray(data) ? data : []).filter(row => row?.managerName !== TOTAL_ROW_NAME);
    return rows.sort((a, b) => {
      if (managerOrder) {
        const ia = managerOrder.get(String(a?.managerName ?? '').trim().toLowerCase()) ?? Number.MAX_SAFE_INTEGER;
        const ib = managerOrder.get(String(b?.managerName ?? '').trim().toLowerCase()) ?? Number.MAX_SAFE_INTEGER;
        if (ia !== ib) return ia - ib;
      }
      return totalAccounts(b) - totalAccounts(a);
    });
  }, [data, managerOrder]);

  const topManagers = managers.slice(0, TOP_MANAGERS_LIMIT);

  const buildOption = (rows: ManagerAccountSummaryRow[]) => {
    const managerNames = rows.map(row => row?.managerName ?? '—');

    const categorySeries = CATEGORY_META.map(cat => ({
      name: cat.label,
      type: 'bar' as const,
      stack: 'accounts',
      barWidth: 22,
      itemStyle: { color: cat.color },
      emphasis: { disabled: true },
      label: {
        show: true,
        color: '#fff',
        fontWeight: 600,
        fontSize: 11,
        formatter: (p: any) => (Number(p?.value) > 0 ? p.value : ''),
      },
      data: rows.map(row => Number(row?.categorySummaryMap?.[cat.key]?.count ?? 0)),
    }));

    return {
      animationDuration: 400,
      grid: { left: 8, right: 24, top: 44, bottom: 44, containLabel: true },
      legend: {
        data: CATEGORY_META.map(c => c.label),
        top: 4,
        itemWidth: 12,
        itemHeight: 12,
        icon: 'roundRect',
        textStyle: { fontSize: 12, color: '#475569' },
      },
      tooltip: {
        trigger: 'axis' as const,
        axisPointer: { type: 'shadow' as const },
        enterable: false,
        renderMode: 'html' as const,
      },
      xAxis: {
        type: 'value' as const,
        name: 'No. of Accounts',
        nameLocation: 'middle' as const,
        nameGap: 30,
        nameTextStyle: { color: '#64748B', fontSize: 12 },
        minInterval: 1,
        splitLine: { lineStyle: { color: '#EEF2F6' } },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'category' as const,
        inverse: true,
        data: managerNames,
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: { color: '#334155', fontWeight: 600, fontSize: 12 },
      },
      series: categorySeries,
    };
  };

  const option = useMemo(() => buildOption(topManagers), [topManagers]);
  const allManagersOption = useMemo(() => buildOption(managers), [managers]);

  const makeChartClickHandler = (rows: ManagerAccountSummaryRow[]) => (params: any) => {
    if (!onCategoryClick || params?.dataIndex == null) return;
    const meta = CATEGORY_META.find(c => c.label === params?.seriesName);
    if (!meta) return;
    const row = rows[params.dataIndex];
    if (!row) return;
    const companies = row?.categorySummaryMap?.[meta.key]?.companies ?? [];
    if (companies.length) {
      onCategoryClick(meta.key, companies, row?.managerName ?? '');
    }
  };

  // Deduplicated company list (with category) for the full report drawer.
  const fullReportCompanies = useMemo(() => {
    const totalRow = (Array.isArray(data) ? data : []).find(row => row?.managerName === TOTAL_ROW_NAME);
    const source = totalRow ? [totalRow] : managers;
    const byId = new Map<string | number, any>();

    source.forEach(row => {
      CATEGORY_META.forEach(cat => {
        const companies = row?.categorySummaryMap?.[cat.key]?.companies ?? [];
        companies.forEach((company: any) => {
          const id = company?.id ?? company?.accountId ?? `${cat.key}-${company?.name}`;
          if (!byId.has(id)) {
            byId.set(id, { ...company, category: cat.key });
          }
        });
      });
    });

    return Array.from(byId.values());
  }, [data, managers]);

  if (!managers.length) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  const chartHeightFor = (count: number) => Math.max(220, count * 56 + 90);

  return (
    <Box>
      <SafeECharts
        option={option}
        style={{ height: chartHeightFor(topManagers.length), width: '100%' }}
        onEvents={{ click: makeChartClickHandler(topManagers) }}
      />

      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {managers.length > TOP_MANAGERS_LIMIT && (
          <Button
            variant="text"
            size="small"
            onClick={() => setViewAllOpen(true)}
            sx={{
              textTransform: 'none',
              color: 'primary.main',
              fontWeight: 600,
              p: 0,
              minWidth: 'auto',
              '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
            }}>
            View all managers ({managers.length})
          </Button>
        )}
        <Button
          variant="text"
          size="small"
          onClick={() => setFullReportOpen(true)}
          endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
          sx={{
            textTransform: 'none',
            color: 'primary.main',
            fontWeight: 600,
            p: 0,
            minWidth: 'auto',
            '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
          }}>
          View Full Report
        </Button>
      </Box>

      <DrawerMenu
        width="70%"
        open={viewAllOpen}
        handleClose={() => setViewAllOpen(false)}
        closeButton
        title="Manager account summary — All managers"
        dropBack>
        <DrawerContent padding={3}>
          <SafeECharts
            option={allManagersOption}
            style={{ height: chartHeightFor(managers.length), width: '100%' }}
            onEvents={{ click: makeChartClickHandler(managers) }}
          />
        </DrawerContent>
      </DrawerMenu>

      <DrawerMenu
        width="70%"
        open={fullReportOpen}
        handleClose={() => setFullReportOpen(false)}
        closeButton
        title="Manager account summary — Full report"
        dropBack>
        <DrawerContent padding={3}>
          {fullReportCompanies.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No companies found.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Company</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }} align="center">
                      Category
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }} align="right">
                      Event Count
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }} align="right">
                      Event Revenue
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }} align="right">
                      Room Count
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }} align="right">
                      Room Revenue
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fullReportCompanies.map((company: any, index: number) => {
                    const meta = CATEGORY_META.find(c => c.key === company.category);
                    return (
                      <TableRow key={company?.id ?? company?.accountId ?? index} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            {company?.name ?? company?.companyName ?? 'Company'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={company.category}
                            size="small"
                            sx={{
                              height: 22,
                              minWidth: 28,
                              fontWeight: 700,
                              fontSize: '0.72rem',
                              color: meta?.color ?? '#475569',
                              backgroundColor: `${meta?.color ?? '#94A3B8'}1A`,
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatNumber(Number(company?.miceEventCount ?? 0))}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatMoney(Number(company?.miceEventPotential ?? 0))}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatNumber(Number(company?.fitRoomNights ?? 0))}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatMoney(Number(company?.fitRoomPotential ?? 0))}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </DrawerContent>
      </DrawerMenu>
    </Box>
  );
};

export default ManagerAccountSummaryChart;
